import { Injectable } from "@angular/core";
import { ActivityType } from "../models/ActivityType";
import { ActivityLogService } from "./activity-log.service";
import { UserService } from "./user.service";
import { LoggingService } from "../utilities/logging.service";
import { getPortalVersion } from "../app.module";
import * as Sentry from '@sentry/angular'

const activityToFeatureName: Partial<Record<ActivityType, string>> = {
    [ActivityType.SignIn]: "Signed In",
    [ActivityType.SignOut]: "Signed Out",
    [ActivityType.ResetPassword]: "Requested Password Reset",

    [ActivityType.OpenNotificationsSection]: "Opened Notification Section",
    [ActivityType.ViewNotificationDetail]: "Viewed Notification Detail",

    [ActivityType.RequestOrderCard]: "Request Order ID Card",
    [ActivityType.DownloadIDCard]: "Download ID Card",
    [ActivityType.FindDoctor]: "Find Doctor",
    [ActivityType.DownloadBillingDocument]: "Download Billing Document",
    [ActivityType.DownloadFormDocument]: "Download Form Document",
    [ActivityType.SendFormDocument]: "Send Form Document",
    [ActivityType.EmailIDCard]: "Email ID Card",
    [ActivityType.SendIDCard]: "Send ID Card",

    [ActivityType.Help]: "Clicked Help Button",
    [ActivityType.ExpandHelp]: "Expanded Help Section",
    [ActivityType.ClickFaqLink]: "Clicked FAQ Link",
    [ActivityType.ClickFaqGuide]: "Clicked FAQ Guide",
    [ActivityType.ClickFaqVideo]: "Clicked FAQ Video",

    [ActivityType.ExportGroupData]: "Export Group Data",
    [ActivityType.GroupView]: "Expanded Group View",
    [ActivityType.MemberView]: "Expanded Member View",
    [ActivityType.GroupSearch]: "Group Search",
    [ActivityType.MemberSearch]: "Member Search",
    [ActivityType.ViewSubscriberType]: "View Subscriber Type",
    [ActivityType.BBPFiltering]: "Broker Business Portal Filtering",
    [ActivityType.ToggleSortingOrder]: "Toggle Sorting Order",
    [ActivityType.FooterLinks]: "Clicked Footer Link",
    [ActivityType.ContactButton]: "Clicked Contact Button",
}

@Injectable({ providedIn: 'root' })
export class TelementryService {
    constructor(
        private logService: ActivityLogService,
        private loggingService: LoggingService,
        private userService: UserService,
    ) {}

    trackActivity(
        activityType: ActivityType,
        actionValue: string = '',
        errorLevel: number = 4,
        featureProps?: Record<string, unknown>,
    ) {
        const userEmail = this.userService.user.email;
        const isBroker = this.userService.isBrokerUser();
        
        this.logService
            .log(userEmail, isBroker, activityType, actionValue)
            .subscribe({
                next: () => {},
                error: (err) => this.loggingService.logException(err, errorLevel)
            });

        const actionValueTrimmed = actionValue?.trim();

        this.trackSentryFeature(activityType, {
            ...(actionValueTrimmed ? { actionValue: actionValueTrimmed } : {}),
            ...(featureProps ?? {}),
        });
    }

    trackSentryFeature(activityType: ActivityType, attributes?: Record<string, unknown>) {
        const featureName = activityToFeatureName[activityType];
        if (featureName) {
            Sentry.logger.info(`Feature used: ${featureName}`, {
                feature: featureName,
                portalType: getPortalVersion(),
                ...(attributes ?? {})
            });
        };
    }
}